import React, { useContext, useCallback, useEffect, useRef, useState } from 'react';

import i18n from '@nextroll/ar-i18n';
import { Alerts, InlineAlert } from '@nextroll/ar-style-base';

import { NotificationContext } from 'hooks/useNotification';
import { useRoflStore } from 'store/roflStore';
import { WORKFLOW_BASE } from 'types/rofl';
import { getContactsByFilterCount, getCreditInfo } from 'api/contactredemptionApi';


/**
 * for all credit usage numbers in the settings page, we want to keep them in the human-readable format
 */
const SettingsCreditLabel = ({ value }: { value: number }) => <span>{i18n.numeral(value).format('0,0')}</span>;

interface IContactCreditsProps {
    totalCost: number;
    totalCount: number;
    remainingCredits: number;
}


/**
 * alert when no contact credits available
 */
const InlineAlertNoAvailableContactCredits = (props: IContactCreditsProps) => {
    const { totalCost, remainingCredits, totalCount } = props;
    return (
        <InlineAlert
            showIcon
            type={Alerts.ALERT_TYPE_WARNING}
            contentEl={(
                <div>
                    <p><strong>{i18n.gettext('You have run out of contact credits')}</strong></p>
                    <p>
                        {i18n.gettext('Credits required: <RequiredCredits /> | Credits Available: <RemainingCredits />',
                            {
                                RequiredCredits: <SettingsCreditLabel value={totalCost} />,
                                RemainingCredits: <SettingsCreditLabel value={Math.max(remainingCredits, 0)} />
                            }
                        )}
                    </p>
                    <p>
                        {i18n.gettext(
                            'You currently have 0 credits, but you are trying to buy <UsageCredits /> contacts. This workflow will <STRONG>not purchase any contacts '
                            + 'for now.</STRONG> Please contact your account manager to add more credits. When credits have been replenished, the workflow '
                            + 'will continue based on contacts that meet the criteria at that time.',
                            {
                                UsageCredits: <SettingsCreditLabel value={totalCount} />,
                                STRONG: <strong />,
                            })}
                    </p>
                </div>
            )}
        />
    );
};


/**
 * alert when credits is in sufficient
 */
const InlineAlertInsufficientCredits = (props: IContactCreditsProps) => {
    const { remainingCredits, totalCost, totalCount } = props;
    return (
        <InlineAlert
            showIcon
            type={Alerts.ALERT_TYPE_WARNING}
            contentEl={(
                <div>
                    <p><strong>{i18n.gettext('Insufficient Credits')}</strong></p>
                    <p>
                        {i18n.gettext('Credits required: <RequiredCredits /> | Credits Available: <RemainingCredits />',
                            {
                                RequiredCredits: <SettingsCreditLabel value={totalCost} />,
                                RemainingCredits: <SettingsCreditLabel value={Math.max(remainingCredits, 0)} />
                            }
                        )}
                    </p>
                    <p>
                        {i18n.gettext(
                            'You currently have <RemainingCredits /> credits, but you are trying to buy <UsageCredits /> contacts. We\'ll go ahead '
                            + 'and purchase <STRONG><RemainingCredits /> contacts</STRONG> with your available credits. To buy more, please contact '
                            + 'your account manager to add more credits. When credits have been replenished, the workflow will continue based on '
                            + 'contacts that meet the criteria at that time.',
                            {
                                RemainingCredits: <SettingsCreditLabel value={remainingCredits} />,
                                UsageCredits: <SettingsCreditLabel value={totalCount} />,
                                STRONG: <strong />,
                            })}
                    </p>
                </div>
            )}
        />
    );
};


export const SettingsSectionCreditUsage = (props: { advertisableEid?: string; readonly?: boolean }) => {
    const { advertisableEid } = props;

    const { addAlert } = useContext(NotificationContext);

    const [crm, base, extraData] = useRoflStore(state => [state.crm, state.base, state.extraData]);

    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [totalCost, setTotalCost] = useState(Number.POSITIVE_INFINITY);
    const [remainingCredits, setRemainingCredits] = useState(0);
    const [noAvailableContactCredits, setNoAvailableContactCredits] = useState(false);
    const [insufficientCredits, setInsufficientCredits] = useState(false);

    /**
     * get the default CRM first, and then use it to get contacts cost and remaining credits
     */
    const fetch = useRef(() => {
        if (!advertisableEid || !extraData?.contactFilterQuery) {
            return;
        }
        (async () => {
            setLoading(true);
            try {
                const maxPerAccount = extraData?.contactMaxPerAccount as number || 20;
                const [
                    { count, cost } = { count: 0, cost: Number.POSITIVE_INFINITY },
                    {
                        allotted_credits,
                        used_credits,
                        overage_allowed
                    } = {
                        allotted_credits: 0,
                        used_credits: 0,
                        overage_allowed: false
                    },
                ] = await Promise.all([
                    getContactsByFilterCount(advertisableEid, crm, maxPerAccount, extraData?.contactFilterQuery),
                    getCreditInfo(advertisableEid),
                ]);
                const availableCredits = Math.max(0, (allotted_credits || 0) - (used_credits || 0));
                setTotalCount(count);
                setTotalCost(cost);
                setRemainingCredits(availableCredits);
                setLoading(false);
                setNoAvailableContactCredits(
                    cost > 0
                    && (used_credits >= allotted_credits && overage_allowed === false)
                );
                setInsufficientCredits((allotted_credits > 0) && (cost > availableCredits));
            } catch (e) {
                addAlert?.({
                    type: Alerts.ALERT_TYPE_ERROR,
                    title: i18n.gettext('Error occurred when finding remaining credits'),
                    message: i18n.gettext('Please try again later.'),
                });
                console.error(e);
                setLoading(false);
            }
        })();
    });
    useEffect(() => {
        fetch.current();
    }, []);

    /**
     * numerial values used in the Credit Usage section, including:
     * contacts count, deducted credits, and remaining credits
     */
    const Label = useCallback(({ value }: { value: number }) => { // eslint-disable-line react/no-unused-prop-types
        if (loading) {
            return <span className='loading-spinner-small' />;
        }
        return <SettingsCreditLabel value={value} />;
    }, [loading]);

    // credit usage section only appears in account-based workflows
    if (base !== WORKFLOW_BASE.ACCOUNTS) {
        return null;
    }

    // when editing existing workflows
    if (props.readonly) {
        return (
            <>
                <p><strong>{i18n.gettext('Credit Usage')}</strong></p>
                <p>{i18n.gettext('This workflow includes contact purchases. When active, each purchase uses 1 credit per contact from your annual quota.')}</p>
            </>
        );
    }

    return (
        <>
            <p><strong>{i18n.gettext('Credit Usage')}</strong></p>
            {noAvailableContactCredits && (
                <InlineAlertNoAvailableContactCredits
                    totalCost={totalCost}
                    remainingCredits={remainingCredits}
                    totalCount={totalCount}
                />
            )}
            {insufficientCredits && !noAvailableContactCredits && (
                <InlineAlertInsufficientCredits
                    remainingCredits={remainingCredits}
                    totalCost={totalCost}
                    totalCount={totalCount}
                />
            )}
            {!noAvailableContactCredits && !insufficientCredits && (
                <p>
                    {i18n.gettext(
                        'This workflow includes contact purchases, with each purchase utilizing 1 credit per contact from your annual quota. '
                        + 'Since <UsageCredits /> contacts qualify, upon turning on this workflow, <STRONG><DeductedCredits /> credits</STRONG> '
                        + 'will be deducted from your remaining <RemainingCredits /> credits. '
                        + 'Please note that this will continue to consume credits from your annual quota as the workflow operates and discovers new contacts.',
                        {
                            UsageCredits: <Label value={totalCount} />,
                            DeductedCredits: <Label value={Math.min(totalCost, remainingCredits)} />,
                            RemainingCredits: <Label value={remainingCredits} />,
                            STRONG: <strong />,
                        },
                    )}
                </p>
            )}
        </>
    );
};
